import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UtilsService } from 'app/shared/utils.service';
import { ScoreService } from '../score.service';
import { EntityTypeEnum } from 'app/API.service';
import { DomainFrameworkService } from '../domain-framework.service';
import { Subscription } from 'rxjs';
import { UiScaleService, getUiScale } from '../services/ui-scale.service';

interface Coordinates {
  x: number;
  y: number;
}

@Component({
  selector: 'cygov-circular-progress',
  templateUrl: './circular-progress.component.html',
  styleUrls: ['./circular-progress.component.scss'],
  standalone: false,
})
export class CircularProgressComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() score: number = 0;
  // Opt-in: the score is still being fetched, so the gauge keeps its shape but runs a grey segment
  // round the track instead of drawing an arc and a figure it does not have yet.
  @Input() isLoading = false;
  @Input() target: number;
  @Input() isFrommutliEntity: boolean = false;
  @Input() isUpperDeck: boolean = false;
  @Input() fgArcThickness: number;
  @Input() average: number = 0;
  @Input() svgHeight = 440;
  @Input() svgWidth = 440;
  @Input() svgYAxis = 0;
  @Input() circleXDivisor = 2;
  @Input() circleYDivisor = 2;
  @Input() showTarget = true;
  @Input() showAverage = false;
  @Input() backgroundCircle = false;
  @Input() remainingProgressColor = 'black';
  @Input() circularProgressHover: boolean = false;
  @Input() bgFill: string = 'none';
  @Input() hoverFill: string = 'none';
  @Input() removeIsTextDependency: boolean;
  @Input() maxValue: number;
  @Input() applyGradient: boolean = UtilsService.isBnB || UtilsService.isCyberArk;
  @Input() isMaturityOption: boolean = false;
  @Input() showIncrementCounter: boolean = false;
  @Input() adjustGaugeHeight: boolean = false; // flag to alter gauge size incase of overview sections
  @Input() rootSelectedTier: any = null;
  @Input() isMidMarket: boolean = null;
  @Input() thirdPartyCheck: boolean = false;
  @Input() activeAssessmentData: any = null;
  @Input() responsiveToContainer: boolean = false;

  @Input() capOrTool: any = {}; // in case of maturity in netskope
  @ViewChild('progressHost', { static: false }) progressHost: ElementRef<HTMLDivElement>;
  @ViewChild('ringSvg', { static: false }) ringSvg: ElementRef<SVGSVGElement>;
  @ViewChild('numberInputTag', { static: false }) inputElement: ElementRef;
  @ViewChild('numberInputTag2', { static: false }) inputElement2: ElementRef;
  @Output() performOperation: EventEmitter<any> = new EventEmitter<any>();
  MIN_SCORE: number = 0;
  MAX_SCORE: number = 10;

  STARTING_ANGLE = 35;
  ENDING_ANGLE = 325;

  // For Overview Vendor Widget only.
  SVG_HEIGHT_OFFSET = -100;
  SVG_WIDTH_OFFSET = -100;
  SVG_X_CROP = 70;
  SVG_Y_CROP = 50;

  BG_ARC_STROKE = 6;
  FG_ARC_STROKE = 12;
  LINE_STROKE = 4;
  ARC_RADIUS = this.adjustGaugeHeight ? 160 : 120;
  LINE_LENGTH = 25;
  SCORE_LABEL_OFFSET = 10;
  svgViewBox: string;
  lineLabelRadius: number;

  availableAngle: number;
  centerX: number;
  centerY: number;

  minScoreAxisX: number;
  minScoreAxisY: number;
  maxScoreAxisX: number;
  maxScoreAxisY: number;
  backgroundArcData: string;
  scoreArcData: string;

  targetLabel: string;
  targetCoordinates: Coordinates;

  averageLabel: string;
  averageCoordinates: Coordinates;

  scoreStrokeClassColor: string;

  gradientID: string = '';

  editNumber = false;
  editNumber2 = false;

  isBeecher: boolean = UtilsService.isBnBCyberSite || UtilsService.isMidMarket;
  isBnb: boolean = UtilsService.isBnB;
  isCyberArk: boolean = UtilsService.isCyberArk;
  utils = UtilsService;
  isHovered: boolean = false; //* to track hover state
  riskFrameworkName: string = null; //* Risk Framework Name to show on hover
  resizeObserver: ResizeObserver;
  textFloorObserver: ResizeObserver;
  viewBoxObserver: MutationObserver;
  uiScaleSub: Subscription;
  baseSvgWidth: number;
  baseSvgHeight: number;
  baseSvgYAxis: number;
  baseArcRadius: number;
  baseLineLength: number;
  baseScoreLabelOffset: number;

  readonly TARGET_INNER_RADIUS = 4;
  readonly TARGET_OUTER_RADIUS = 8;

  constructor(
    private toastrService: ToastrService,
    private scoreService: ScoreService,
    private domainFrameworkService: DomainFrameworkService,
    private uiScaleService: UiScaleService,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.MAX_SCORE = this.maxValue ? this.maxValue : 10;
    if (this.score < this.MIN_SCORE || this.score > this.MAX_SCORE) {
      this.toastrService.error('Invalid Score Value');
      return;
    }
    this.baseSvgWidth = this.svgWidth;
    this.baseSvgHeight = this.svgHeight;
    this.baseSvgYAxis = this.svgYAxis;
    this.baseArcRadius = this.ARC_RADIUS;
    this.baseLineLength = this.LINE_LENGTH;
    this.baseScoreLabelOffset = this.SCORE_LABEL_OFFSET;
    this.initializeAttributes();

    if (!this.showTarget && !this.isUpperDeck) {
      this.updateSVGViewBox();
    }

    this.setBackgroundArc();
    this.setScoreArc();
    this.setTarget();
    this.setAverage();
    if (this.activeAssessmentData) {
      this.riskFrameworkName = this.getRiskFrameworkName();
    }
  }

  ngAfterViewInit(): void {
    this.observeTextFloor();

    if (!this.responsiveToContainer || !this.progressHost) {
      return;
    }

    this.updateResponsiveGeometry(this.progressHost.nativeElement.clientWidth);

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(entries => {
      const hostWidth = entries[0]?.contentRect.width || this.progressHost.nativeElement.clientWidth;
      this.updateResponsiveGeometry(hostWidth);
    });
    this.resizeObserver.observe(this.progressHost.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.textFloorObserver?.disconnect();
    this.viewBoxObserver?.disconnect();
    this.uiScaleSub?.unsubscribe();
  }

  // The ring is drawn in a viewBox, so its small labels shrink with it (9px at 1280). Exposes the
  // user-unit size of one screen px and a label boost so the SCSS can keep them at 12px or more.
  private observeTextFloor(): void {
    if (!this.ringSvg || !this.progressHost) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.uiScaleSub = this.uiScaleService.effectiveScale$.subscribe(() =>
        requestAnimationFrame(() => this.updateTextFloor())
      );
      if (typeof ResizeObserver !== 'undefined') {
        this.textFloorObserver = new ResizeObserver(() => this.updateTextFloor());
        this.textFloorObserver.observe(this.ringSvg.nativeElement);
      }
      // a viewBox change rescales the ring without resizing the svg box, so the observer above misses it
      this.viewBoxObserver = new MutationObserver(() => this.updateTextFloor());
      this.viewBoxObserver.observe(this.ringSvg.nativeElement, { attributes: true, attributeFilter: ['viewBox'] });
    });
  }

  private updateTextFloor(): void {
    const svg = this.ringSvg?.nativeElement;
    const host = this.progressHost?.nativeElement;
    const viewBox = svg?.viewBox?.baseVal;
    // layout size, not getBoundingClientRect: entrance animations transform the box without resizing it
    const rect = svg && { width: svg.clientWidth, height: svg.clientHeight };
    if (!host || !viewBox?.width || !viewBox?.height || !rect?.width || !rect?.height) {
      return;
    }
    const ratio = Math.min(rect.width / viewBox.width, rect.height / viewBox.height);
    host.style.setProperty('--svg-unit-px', `${1 / ratio}`);

    const label = svg.querySelector('.arc-min-score');
    const labelPx = label ? parseFloat(getComputedStyle(label).fontSize) * ratio : 0;
    // floor follows Display Size; capped so a thumbnail-sized ring (report previews) doesn't get labels larger than its arc
    const boost = labelPx ? Math.min(Math.max(1, (12 * getUiScale()) / labelPx), 1.4) : 1;
    host.style.setProperty('--svg-label-boost', `${boost}`);
  }

  ngOnChanges(changes): void {
    this.ARC_RADIUS = this.adjustGaugeHeight ? 160 : 120;
    if (changes) {
      // This will update the score on updating the tier
      if (UtilsService.isDefined(changes.score) && this.rootSelectedTier && !this.isFrommutliEntity) {
        this.score = UtilsService.calculatePillarsScoreByTier(this.score, this.rootSelectedTier);
      }
      // if score change is detected then update score on widget
      if (changes.score) {
        this.setScoreArc();
      }
      // if target change is detected then update target on widget
      if (changes.target) {
        this.setTarget();
      }
      // if average change is detected then update average on widget
      if (changes.average) {
        this.setAverage();
      }
    }
  }

  private updateResponsiveGeometry(hostWidth: number): void {
    if (!hostWidth || !this.baseSvgWidth || !this.baseSvgHeight) {
      return;
    }

    const nextWidth = Math.max(260, Math.min(hostWidth, 560));
    const scale = nextWidth / this.baseSvgWidth;

    this.svgWidth = Math.round(nextWidth);
    this.svgHeight = Math.round(this.baseSvgHeight * scale);
    this.svgYAxis = Math.round(this.baseSvgYAxis * scale);
    this.ARC_RADIUS = Math.round(this.baseArcRadius * scale);
    this.LINE_LENGTH = Math.round(this.baseLineLength * scale);
    this.SCORE_LABEL_OFFSET = Math.max(8, Math.round(this.baseScoreLabelOffset * scale));

    this.initializeAttributes();
    this.setBackgroundArc();
    this.setScoreArc();
    this.setTarget();
    this.setAverage();

    if (!this.showTarget && !this.isUpperDeck) {
      this.updateSVGViewBox();
    }
  }

  private initializeAttributes() {
    this.svgViewBox = `0 ${this.svgYAxis} ${this.svgWidth} ${this.svgHeight}`;
    this.lineLabelRadius = this.ARC_RADIUS + this.LINE_LENGTH + 15;
    this.availableAngle = this.ENDING_ANGLE - this.STARTING_ANGLE;
    this.centerX = this.svgWidth / this.circleXDivisor;
    // A responsive gauge sits centred in the viewBox. The offsets below are measured from the
    // top of the box rather than its middle, so the arc ends up high in it - 9 units of air above
    // the arc against 21 below at the base size, and scaling exaggerates that: at 1366 the arc
    // clears the top of the box by 3 units and the bottom by 20, which reads as the gauge hugging
    // the top edge of the card. The viewBox runs from svgYAxis to svgYAxis + svgHeight, so its
    // middle is the sum below. Fixed gauges keep the offsets they have.
    if (this.responsiveToContainer) {
      this.centerY = this.svgYAxis + this.svgHeight / 2;
    } else {
      this.centerY = this.adjustGaugeHeight
        ? this.svgHeight / this.circleYDivisor - 35
        : this.svgHeight / this.circleYDivisor - 10;
    }
  }

  private setBackgroundArc() {
    this.backgroundArcData = this.describeArc(
      this.centerX,
      this.centerY,
      this.ARC_RADIUS,
      this.STARTING_ANGLE,
      this.ENDING_ANGLE
    );

    const minScoreAxis = this.polarToCartesian(
      this.centerX,
      this.centerY,
      this.ARC_RADIUS,
      this.STARTING_ANGLE - this.SCORE_LABEL_OFFSET
    );
    this.minScoreAxisX = minScoreAxis.x;
    this.minScoreAxisY = minScoreAxis.y;

    const maxScoreAxis = this.polarToCartesian(
      this.centerX,
      this.centerY,
      this.ARC_RADIUS,
      this.ENDING_ANGLE + this.SCORE_LABEL_OFFSET
    );
    this.maxScoreAxisX = maxScoreAxis.x;
    this.maxScoreAxisY = maxScoreAxis.y + 10;
  }

  private setScoreArc() {
    let relativeScore = this.score * (this.availableAngle / this.MAX_SCORE) + this.STARTING_ANGLE;
    if (isNaN(relativeScore)) {
      relativeScore = 0;
    }
    this.scoreArcData = this.describeArc(
      this.centerX,
      this.centerY,
      this.ARC_RADIUS,
      this.STARTING_ANGLE,
      relativeScore
    );
    // * sets color based on flags
    if (this.isBnb) {
      this.scoreStrokeClassColor = this.getBnbScoreClass(this.score);
    } else if (this.isCyberArk) {
      this.scoreStrokeClassColor = this.getCyberArkScoreClass(this.score);
    } else {
      this.scoreStrokeClassColor = this.getScoreClass(this.score);
    }
  }

  private setTarget() {
    let targetAngle = this.target * (this.availableAngle / 10) + this.STARTING_ANGLE;
    if (isNaN(targetAngle)) {
      targetAngle = 0;
    }
    this.targetCoordinates = this.describeCircle(this.centerX, this.centerY, this.ARC_RADIUS, targetAngle);
    // this.targetLabel = this.getTextLabel('Target:', targetAngle);
    this.targetLabel = 'Target:';
    const scaledTarget = this.scaleScore(this.target);
    this.targetLabel = this.targetLabel ? `${this.targetLabel} ${scaledTarget}` : this.targetLabel;
    /*
    Actually target bubble is not showing in a set of specific ranges of angle. So,
    Before that: if angle lies in any of those ranges, "getTextLabel" was now showing the label and bubble.
    Now: I am shifting the values from invalid range to the valid range.
    */
    targetAngle = this.setTargetRange(targetAngle);
  }

  private setAverage(): void {
    let avgAngle = this.average * (this.availableAngle / this.MAX_SCORE) + this.STARTING_ANGLE;
    this.averageCoordinates = this.describeCircle(this.centerX, this.centerY, this.ARC_RADIUS, avgAngle);
    // this.averageLabel = this.getTextLabel('Average:', avgAngle);
    this.averageLabel = 'Average:';
    this.averageLabel = this.averageLabel ? `${this.averageLabel} ${this.average.toFixed(1)}` : this.averageLabel;
    //
    avgAngle = this.setTargetRange(avgAngle);
  }

  private getScoreClass(score: number): string {
    let className = null;
    const s = score / this.MAX_SCORE;
    if (this.isBeecher) {
      switch (true) {
        case s < 0.7:
          className = 'score-low-stroke';
          this.gradientID = 'url(#gradientRed)';
          break;
        case s < 0.8:
          className = 'score-medium-stroke';
          this.gradientID = 'url(#gradientYellow)';
          break;
        case s >= 0.8:
          className = 'score-high-stroke';
          this.gradientID = 'url(#gradientGreen)';
          break;
      }
    } else {
      switch (true) {
        case s <= 0.33:
          className = 'score-low-stroke';
          this.gradientID = 'url(#gradientRed)';
          break;
        case s < 0.66:
          className = 'score-medium-stroke';
          this.gradientID = 'url(#gradientYellow)';
          break;
        case s >= 0.66:
          className = 'score-high-stroke';
          this.gradientID = 'url(#gradientGreen)';
          break;
      }
    }
    return className ? className : '';
  }

  private getBnbScoreClass(score: number): string {
    let className = null;
    const s = score / this.MAX_SCORE;
    switch (true) {
      case s <= 0.4:
        className = 'bnb-score-lowest-stroke';
        this.gradientID = 'url(#gradientBnbRed)';
        break;
      case s <= 0.6:
        className = 'bnb-score-low-stroke';
        this.gradientID = 'url(#gradientBnbOrgange)';
        break;
      case s <= 0.8:
        className = 'bnb-score-medium-stroke';
        this.gradientID = 'url(#gradientBnbGold)';
        break;
      case s < 1:
        className = 'bnb-score-high-stroke';
        this.gradientID = 'url(#gradientBnbGreen)';
        break;
      case s === 1:
        className = 'bnb-score-highest-stroke';
        this.gradientID = 'url(#gradientBnbBlue)';
        break;
    }
    return className ? className : '';
  }

  // * returns CyberArk related classes
  private getCyberArkScoreClass(score: number): string {
    let className = null;
    const s = score / this.MAX_SCORE;
    switch (true) {
      case s > 0.9:
        className = 'cyberArk-score-high-stroke';
        this.gradientID = 'url(#gradientGreen)';
        break;
      case s > 0.5:
        className = 'cyberArk-score-medium-stroke';
        this.gradientID = 'url(#gradientYellow)';
        break;
      case s >= 0:
        className = 'cyberArk-score-low-stroke';
        this.gradientID = 'url(#gradientRed)';
        break;
    }
    return className ? className : '';
  }

  private getLabelAnchor(angle): string {
    if (angle <= 70) {
      return 'start';
    } else if (angle <= 100) {
      return 'middle';
    } else if (angle <= 260) {
      return 'end';
    } else if (angle <= 280) {
      return 'middle';
    } else {
      return 'start';
    }
  }

  // private getTextLabel(label: string, angle: number): string {
  //   // I intentionally change lowest angle from 50 to 10, because our lowest value is 0/0.1 and 0.1 is equal to 36.5
  //   // which is less than 50
  //   // Also
  //   // changing first hightest angle limit of first condition from 133 to 209 to show target for lowest values
  //   // as well
  //   if ((angle >= 10 && angle <= 209) || (angle >= 230 && angle <= 315)) {
  //     return label;
  //   }
  //   return '';
  // }

  private setTargetRange(angle: number): number {
    // Comparing with the ranges in which target is not showing.
    if (angle < 10) {
      angle = 10;
    } else if (angle > 209 && angle <= 220) {
      angle = 209;
    } else if (angle > 220 && angle < 230) {
      angle = 230;
    } else if (angle > 315) {
      angle = 315;
    }

    return angle;
  }

  private polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }

  private describeArc(x, y, radius, startAngle, endAngle) {
    const start = this.polarToCartesian(x, y, radius, endAngle);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const arcSweep = endAngle - startAngle <= 180 ? '0' : '1';
    const d = ['M', start.x, start.y, 'A', radius, radius, 0, arcSweep, 0, end.x, end.y].join(' ');
    return d;
  }

  private describeCircle(x, y, radius, angle): Coordinates {
    const startLine = this.polarToCartesian(x, y, radius, angle);
    return { x: startLine.x, y: startLine.y };
  }

  private updateSVGViewBox(): void {
    this.svgViewBox = `${this.SVG_X_CROP} ${this.SVG_Y_CROP} ${this.svgWidth + this.SVG_WIDTH_OFFSET} ${
      this.svgHeight + this.SVG_HEIGHT_OFFSET
    }`;
  }
  performIncDecOperation(id, type, isMaturity: boolean = true) {
    this.performOperation.next({ id, type, isMaturity });
  }

  showEditNumber(isTarget = false): void {
    if (isTarget) {
      this.editNumber2 = true;
      setTimeout(() => {
        this.inputElement2.nativeElement.focus();
      }, 0);
    } else {
      this.editNumber = true;
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 0);
    }
  }

  valueCheck(value, isTarget = false): void {
    if (!UtilsService.isDefined(value) || value < 0) {
      if (isTarget) {
        this.capOrTool.target = 0;
        if (this.inputElement) {
          this.inputElement2.nativeElement.value = '0';
        }
      } else {
        this.capOrTool.maturity = 0;
        if (this.inputElement) {
          this.inputElement.nativeElement.value = '0';
        }
      }
    } else {
      if (value > 5) {
        value = 5;
      }
      if (Math.ceil(value % 1)) {
        const gf = value + '';
        const decimalPartArray = gf.toString().split('.');
        setTimeout(() => {
          let toEmit;
          if (decimalPartArray[1].length > 1) {
            toEmit = parseFloat(decimalPartArray[0] + '.' + decimalPartArray[1].slice(0, 1));
          } else if (decimalPartArray[1].length === 1) {
            toEmit = parseFloat(decimalPartArray[0] + '.' + decimalPartArray[1]);
          }
          if (isTarget) {
            this.capOrTool.target = toEmit;
          } else {
            this.capOrTool.maturity = toEmit;
          }
        }, 0);
      } else {
        if (isTarget) {
          if (this.inputElement2) {
            this.inputElement2.nativeElement.value = typeof value === 'string' ? value : JSON.stringify(value);
          }
          this.capOrTool.target = value;
        } else {
          if (this.inputElement) {
            this.inputElement.nativeElement.value = typeof value === 'string' ? value : JSON.stringify(value);
          }
          this.capOrTool.maturity = value;
        }
      }
    }
  }

  private scaleScore(score: any): any {
    return this.scoreService.getScaledScore(score, this.thirdPartyCheck);
  }

  //* Get framework name from key
  private getFrameworkName(val: string): string {
    const isFrameworkKey = this.domainFrameworkService.isFrameworkKey(val);
    return isFrameworkKey ? this.domainFrameworkService.getFrameworkName(val) : val;
  }

  //* Get active risk framework name
  private getRiskFrameworkName(): string {
    const frameworks = this.activeAssessmentData?.standardFrameworkList?.items || [];
    const activeFramework = frameworks.find(
      fw => fw?.type === EntityTypeEnum.RISK_FRAMEWORK && !fw?.not_added && !fw?.archived
    );

    return activeFramework ? this.getFrameworkName(activeFramework.key) : '';
  }
}
